import { Chip, Paper, Typography } from '@mui/material'

import { Box, InfoRow } from '@/components/ui'
import { TIMEZONE_OPTIONS } from '@/definitions/location'
import { formatPhoneNumber } from '@/utils/phoneUtils'

import type { LocationRow } from '../../utils/buildLocationRows'

interface LocationCardProps {
  row: LocationRow
}

/** The friendly zone name, falling back to the stored value if it is not one we offer. */
const timezoneLabel = (timezone: string | null): string => {
  if (!timezone) {
    return ''
  }
  return TIMEZONE_OPTIONS.find((option) => option.value === timezone)?.label ?? timezone
}

const addressLine = (street: string | null, suite: string | null): string =>
  [street, suite].filter(Boolean).join(', ')

const cityLine = (
  city: string | null,
  state: string | null,
  postalCode: string | null
): string => {
  const cityState = [city, state].filter(Boolean).join(', ')
  return [cityState, postalCode].filter(Boolean).join(' ')
}

/**
 * One location, read-only.
 *
 * Every field renders even when the location has no value for it, so a gap in a
 * practice's record is visible rather than silently absent — `InfoRow` shows an em
 * dash for a blank value. There is deliberately no edit or disable control:
 * location details are self-service in Bartimaeus.
 *
 * The card's own border is what separates one location from the next, so it carries
 * its whole content — nothing is expected to sit outside it.
 */
export const LocationCard = ({ row }: LocationCardProps) => {
  const { location, isPrimary } = row

  return (
    <Paper variant='detailCard'>
      <Box variant='flexColumn' gap='sm'>
        <Box variant='flex' gap='sm'>
          <Typography variant='overline' color='text.secondary'>
            {location.display_name}
          </Typography>
          {isPrimary && <Chip variant='status' label='Primary' color='info' size='small' />}
          <Chip
            variant='status'
            label={location.enabled ? 'Active' : 'Deactivated'}
            color={location.enabled ? 'success' : 'error'}
            size='small'
          />
        </Box>

        <Box variant='flexColumn' gap='sm'>
          <InfoRow label='Street' value={addressLine(location.street, location.suite)} />
          <InfoRow
            label='City, State ZIP'
            value={cityLine(location.city, location.state, location.postal_code)}
          />
          <InfoRow
            label='Phone'
            value={location.primary_phone ? formatPhoneNumber(location.primary_phone) : ''}
          />
          <InfoRow
            label='Time Zone'
            value={timezoneLabel(location.timezone)}
            tooltip='Drives appointment times and reminder sends for this location'
          />
        </Box>
      </Box>
    </Paper>
  )
}
